import { createServerFn } from '@tanstack/react-start'

import { listStorefrontTestimonials, normalizeStorefrontTestimonial } from './dropify'
import type {
  StorefrontTestimonialListParams,
  StorefrontTestimonialMedia,
} from './dropify'

export type StorefrontTestimonialMediaFeed = {
  items: StorefrontTestimonialMedia[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export function normalizeTestimonialFeedResponse(
  input: unknown,
  page: number,
  perPage: number,
): StorefrontTestimonialMediaFeed {
  const payload = Array.isArray(input)
    ? { items: input, total: input.length, page, per_page: perPage, total_pages: input.length > 0 ? 1 : 0 }
    : input && typeof input === 'object'
      ? input as Record<string, unknown>
      : { items: [], total: 0, page, per_page: perPage, total_pages: 0 }

  const rawItems = Array.isArray(payload.items) ? payload.items : []
  const mediaItems = rawItems
    .map((item) => (item && typeof item === 'object' ? normalizeStorefrontTestimonial(item as Record<string, unknown>) : null))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .flatMap((testimonial) => testimonial.is_active ? testimonial.media : [])
    .sort((a, b) => b.sortKey - a.sortKey)

  const total = typeof payload.total === 'number' ? payload.total : mediaItems.length
  const totalPages = typeof payload.total_pages === 'number'
    ? payload.total_pages
    : typeof payload.per_page === 'number' && payload.per_page > 0
      ? Math.ceil(total / payload.per_page)
      : (mediaItems.length > 0 ? 1 : 0)

  return {
    items: mediaItems,
    total,
    page: typeof payload.page === 'number' ? payload.page : page,
    perPage: typeof payload.per_page === 'number' ? payload.per_page : perPage,
    totalPages,
  }
}

export const listStorefrontTestimonialsServer = createServerFn({ method: 'GET' })
  .inputValidator((params: StorefrontTestimonialListParams) => params)
  .handler(async ({ data }) => normalizeTestimonialFeedResponse(
    await listStorefrontTestimonials(data),
    data.page ?? 1,
    data.per_page ?? 4,
  ))

export async function getBrandProofImages(limit = 12): Promise<string[]> {
  const response = await listStorefrontTestimonials({ page: 1, per_page: 6 }).catch(() => [])
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response.items)
      ? response.items
      : []

  return rows
    .map((row) => normalizeStorefrontTestimonial(row))
    .filter((testimonial) => testimonial.is_active)
    .flatMap((testimonial) => testimonial.media)
    .filter((media) => media.media_type === 'image')
    .sort((a, b) => b.sortKey - a.sortKey)
    .slice(0, limit)
    .map((media) => media.url)
}

export const getBrandProofImagesServer = createServerFn({ method: 'GET' })
  .handler(() => getBrandProofImages())
